import { useMutation } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';
import { isApiError } from '@/lib/api/errors';
import { usePricebookApi } from './usePricebookApi';
import type { ImageUploadUrlRequest, ImageUploadUrlResponse } from '@/types/pricebook';

/**
 * NOT-53 diagnostic: when the S3 PUT or the presign request fails, surface
 * the actual status + a short body excerpt so the next bug report includes
 * enough context to pinpoint the cause (signature mismatch, CORS, bucket
 * policy, allowed-mime check, etc.) instead of the historical generic
 * `Failed to upload image` toast.
 *
 * Kept as a pure function so it can be unit-tested without spinning up
 * the full react-query + axios stack.
 */
export function formatPresignError(error: unknown): string {
  if (isApiError(error)) {
    const detail = error.message ? `: ${error.message}` : '';
    return `Upload setup failed (HTTP ${error.status})${detail}`;
  }
  if (error instanceof Error) return `Upload setup failed: ${error.message}`;
  return 'Upload setup failed';
}

export function formatS3UploadError(status: number, statusText: string, body: string): string {
  // S3 errors come back as XML. Pulling the <Code>/<Message> tags lets the
  // toast read like a sentence instead of dumping the whole envelope.
  const codeMatch = body.match(/<Code>([^<]+)<\/Code>/i);
  const messageMatch = body.match(/<Message>([^<]+)<\/Message>/i);
  const summary = [codeMatch?.[1], messageMatch?.[1]].filter(Boolean).join(': ');
  const detail = summary
    ? summary
    : body
      ? truncate(body.replace(/\s+/g, ' ').trim(), 200)
      : statusText || 'no response body';
  return `S3 upload failed (HTTP ${status}): ${detail}`;
}

function truncate(input: string, max: number): string {
  return input.length <= max ? input : `${input.slice(0, max - 1)}…`;
}

export function useUploadPricebookImage() {
  const { client, ep } = usePricebookApi();

  return useMutation<string, Error, { id: string; file: File }>({
    mutationFn: async ({ id, file }) => {
      // Step 1: get presigned upload URL
      const req: ImageUploadUrlRequest = {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      };
      let res: ImageUploadUrlResponse;
      try {
        res = await apiPost<ImageUploadUrlResponse>(client, ep.imageUploadUrl(id), req);
      } catch (error) {
        throw new Error(formatPresignError(error));
      }

      // Step 2: PUT file directly to S3
      const s3Res = await fetch(res.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!s3Res.ok) {
        const body = await s3Res.text().catch(() => '');
        throw new Error(formatS3UploadError(s3Res.status, s3Res.statusText, body));
      }

      // Return the fileKey to store as imageUrl
      return res.attachment.fileKey;
    },
    // Intentionally NO `onError` toast here — callers wrap `mutateAsync` in
    // their own try/catch and decide whether the failure should warn the
    // user (e.g. "item created but image upload failed") or block the form
    // submit. A toast at this layer would fire in addition to the caller's
    // handler, producing duplicate banners.
  });
}
